import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import StickyQuoteButton from "@/components/feature/StickyQuoteButton";
import { useSEO } from "@/hooks/useSEO";

const products = [
  {
    id: "sc880",
    name: "Hytera SC880",
    badge: "Flagship",
    tagline: "5G Smart Body Camera — 4K, 300s Pre-Record, IP68, MIL-STD-810H",
    image: "https://static.readdy.ai/image/8ef50732a893e16bbc98824684ca939c/0cb7f09f1fcbcac9b09d7997834340e7.png",
    specs: [
      { label: "Video Resolution", value: "4K (2160p), 2K, 1080p, 720p" },
      { label: "Battery Life", value: "10 hrs @ 1080p (5G + GPS on)" },
      { label: "Pre-Recording", value: "300 seconds pre/post-event" },
      { label: "Connectivity", value: "5G, Wi-Fi 6 (802.11ax), BT 5.2, NFC" },
      { label: "GPS", value: "GPS / BDS / GLONASS / GALILEO" },
      { label: "Durability", value: "IP68, MIL-STD-810H, 1.5m drop" },
      { label: "Screen", value: "2.4-inch touchscreen (480×640)" },
      { label: "Storage", value: "4GB RAM + 64GB ROM" },
    ],
    features: [
      "5G high-bandwidth, low-latency real-time video transmission",
      "4K Ultra HD video with 130° ultra-wide FOV and minimal 5% distortion",
      "300-second pre/post-event recording — never miss critical footage",
      "Dual high-performance microphones with AI noise cancellation — captures audio up to 10 metres",
      "Anti-falling battery design with robust latch mechanism",
      "USB 3.0 Type-C — offloads 40GB of evidence in just 2 hours",
      "AES-256 encryption with digital signature and hash verification",
      "Intelligent scene-aware: camera-blocked and man-down auto-alerts",
    ],
  },
  {
    id: "sc780",
    name: "Hytera SC780",
    badge: "Popular",
    tagline: "4G LTE Smart Body Camera — 13-Hour Battery, 120s Pre-Record",
    image: "https://static.readdy.ai/image/8ef50732a893e16bbc98824684ca939c/ce581c5e95e949b46179572a17de2edb.webp",
    specs: [
      { label: "Video Resolution", value: "1440P (2K), 1080P, 720P, 480P @ 30fps" },
      { label: "Battery Life", value: "13 hours @ 720P (GPS + LTE on)" },
      { label: "Pre-Recording", value: "120 seconds pre/post-event" },
      { label: "Connectivity", value: "4G LTE, Wi-Fi 802.11a/b/g/n/ac, BT 5.1, NFC" },
      { label: "GPS", value: "GPS / BDS / GLONASS / GALILEO" },
      { label: "Durability", value: "IP68, MIL-STD-810H, CE, IC, 1.5m drop" },
      { label: "Field of View", value: "130° Diagonal" },
      { label: "Storage", value: "32GB / 64GB / 128GB / 256GB" },
    ],
    features: [
      "6-axis image stabilization for rock-steady video while walking or running",
      "130° ultra-wide FOV captures full scene context",
      "120-second pre/post-event recording for complete incident narratives",
      "Real-time HD live streaming with Adaptive Bitrate (ABR) technology",
      "Dual-stream: saves 1080P locally while streaming compressed video to command",
      "NFC-enabled officer ID switch — instant account swap at shift change",
      "Dual 95dB speakers with AI-based noise cancellation and howling suppression",
      "PTT radio over cellular — communicate with DMR/TETRA radios via Hytera system",
    ],
  },
  {
    id: "gc550",
    name: "Hytera GC550",
    badge: "Compact",
    tagline: "Compact Body Camera — 2K, 9-Hour Battery, IR Night Vision",
    image: "https://static.readdy.ai/image/8ef50732a893e16bbc98824684ca939c/4c9b9e898793c8c887b16e91f8744d95.png",
    specs: [
      { label: "Video Resolution", value: "2K (1440P), 1080P, 720P, 480P @ 30fps" },
      { label: "Battery Life", value: "9 hours @ 720P continuous" },
      { label: "Pre-Recording", value: "120 seconds pre-event" },
      { label: "Durability", value: "IP67, MIL-STD-810G" },
      { label: "Field of View", value: "120° Wide Angle" },
      { label: "Night Vision", value: "IR + Starlight low-light" },
      { label: "Storage", value: "32GB / 64GB / 128GB" },
      { label: "Encryption", value: "AES-256 encrypted storage" },
    ],
    features: [
      "Compact, lightweight design — easy to wear for extended shifts",
      "2K (1440P) Ultra HD video for sharp, court-admissible evidence",
      "120-second pre-event buffer — capture what happened before recording started",
      "IP67 waterproof and MIL-STD-810G certified for demanding field conditions",
      "Infrared night vision — captures usable footage in low-light environments",
      "AES-256 encryption ensures evidence integrity and chain of custody",
      "Intuitive one-button recording for fast, stress-free operation",
      "Compatible with Hytera DEMS evidence management platform",
    ],
  },
];

const useCases = [
  {
    icon: "ri-shield-user-line",
    title: "Royal Malaysia Police (PDRM)",
    desc: "Deployed across patrol units for evidence capture, public accountability, and incident documentation. Reduces evidence disputes and strengthens prosecution cases.",
    stat: "68% reduction in evidence disputes",
  },
  {
    icon: "ri-building-4-line",
    title: "Government Security Agencies",
    desc: "Used by enforcement officers in customs, immigration, and regulatory bodies for field operations, inspection documentation, and officer safety.",
    stat: "100% tamper-evident audit trail",
  },
  {
    icon: "ri-store-2-line",
    title: "Private Security Companies",
    desc: "Deployed by security firms protecting commercial properties, malls, and industrial sites — providing accountability and deterrence.",
    stat: "40% reduction in security incidents",
  },
  {
    icon: "ri-hospital-line",
    title: "Healthcare Security",
    desc: "Protecting hospital staff and patients in high-risk environments, with footage used for incident review and staff safety protocols.",
    stat: "Improved staff safety compliance",
  },
];

const whyHytera = [
  { icon: "ri-award-line", title: "Malaysia's Primary BWC Choice", desc: "Hytera is the leading body-worn camera brand deployed by law enforcement and security agencies across Malaysia." },
  { icon: "ri-shield-keyhole-line", title: "End-to-End Encryption", desc: "AES-256 encrypted video from capture to storage — ensuring chain-of-custody integrity for legal admissibility." },
  { icon: "ri-customer-service-2-line", title: "Local Authorized Support", desc: "SE Distribution provides full local warranty, deployment support, and ongoing maintenance from our KL office." },
  { icon: "ri-global-line", title: "Global Proven Track Record", desc: "Hytera BWC devices are trusted by 120+ countries and millions of frontline officers worldwide." },
  { icon: "ri-database-2-line", title: "Evidence Management Platform", desc: "Integrated digital evidence management system for secure upload, tagging, search, and audit trails." },
  { icon: "ri-settings-3-line", title: "Scalable Deployment", desc: "From 10 units to 10,000 — Hytera's ecosystem scales with your organization's growth and operational needs." },
];

const hyteraFAQSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What is the difference between SC880 5G, SC780 4G, and GC550?",
      "acceptedAnswer": { "@type": "Answer", "text": "The SC880 5G is the flagship with 4K video, 5G streaming and 300s pre-record. The SC780 4G offers the longest battery (13hrs) with 4G live streaming. The GC550 is the compact 1080P option with one-button operation for cost-effective wide deployments. All three feature AES-256 encryption and DEMS compatibility." }
    },
    {
      "@type": "Question",
      "name": "Which Hytera BWC model is recommended for PDRM and Malaysian police?",
      "acceptedAnswer": { "@type": "Answer", "text": "For frontline PDRM patrol, the SC880 5G is recommended for 4K evidence and 5G command streaming. For extended patrols, the SC780 4G's 13-hour battery excels. The GC550 is ideal for auxiliary police and station personnel needing basic incident recording." }
    },
    {
      "@type": "Question",
      "name": "Is the GC550 suitable for private security and retail deployments?",
      "acceptedAnswer": { "@type": "Answer", "text": "Yes. The GC550 is ideal for retail security, mall officers, hospital security, transit staff, and corporate campuses. Its simple one-button operation, compact clip-on design, and AES-256 encryption make it perfect for large security workforces." }
    },
    {
      "@type": "Question",
      "name": "Can the SC880, SC780 and GC550 all work with Hytera DEMS?",
      "acceptedAnswer": { "@type": "Answer", "text": "Yes. All three models are fully compatible with Hytera DEMS. When docked, footage auto-uploads with GPS, officer ID, timestamp and incident tags creating a tamper-proof chain of custody for legal proceedings. DEMS supports on-premise or private cloud deployment." }
    },
    {
      "@type": "Question",
      "name": "What is the battery life of each Hytera BWC model?",
      "acceptedAnswer": { "@type": "Answer", "text": "SC880 5G: 10 hours at 1080P with 5G and GPS active. SC780 4G: 13 hours at 720P — the longest battery in the lineup. GC550: 9 hours at 720P. All support multi-bay docking for overnight charging and auto DEMS upload." }
    }
  ]
};

const hyteraProductSchema = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  "name": "Hytera Body-Worn Camera Range — SE Distribution Malaysia",
  "description": "Hytera BWC lineup available from SE Distribution, Malaysia's authorized Hytera distributor.",
  "url": "https://www.sedistribution.com.my/hytera-bwv",
  "itemListElement": [
    {
      "@type": "ListItem",
      "position": 1,
      "item": {
        "@type": "Product",
        "name": "Hytera SC880 5G Body-Worn Camera",
        "description": "Flagship 5G body-worn camera with 4K Ultra HD video, 300-second pre/post-event recording, 10-hour battery, IP68, MIL-STD-810H, AES-256 encryption.",
        "brand": { "@type": "Brand", "name": "Hytera" },
        "sku": "SC880",
        "url": "https://www.sedistribution.com.my/hytera-bwv",
        "additionalProperty": [
          { "@type": "PropertyValue", "name": "Video Resolution", "value": "4K (2160p)" },
          { "@type": "PropertyValue", "name": "Battery Life", "value": "10 hours" },
          { "@type": "PropertyValue", "name": "Pre-Recording", "value": "300 seconds" },
          { "@type": "PropertyValue", "name": "Connectivity", "value": "5G, Wi-Fi 6, BT 5.2, NFC" },
          { "@type": "PropertyValue", "name": "Durability", "value": "IP68, MIL-STD-810G" }
        ],
        "offers": {
          "@type": "Offer",
          "availability": "https://schema.org/InStock",
          "priceCurrency": "MYR",
          "seller": { "@type": "Organization", "name": "SE Distribution Sdn Bhd" }
        },
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "4.9",
          "reviewCount": "38",
          "bestRating": "5"
        }
      }
    },
    {
      "@type": "ListItem",
      "position": 2,
      "item": {
        "@type": "Product",
        "name": "Hytera SC780 4G Body-Worn Camera",
        "description": "4G LTE smart body camera with 13-hour battery, 120s pre-record, 6-axis image stabilization, 130° FOV, IP68, MIL-STD-810H.",
        "brand": { "@type": "Brand", "name": "Hytera" },
        "sku": "SC780",
        "url": "https://www.sedistribution.com.my/hytera-bwv",
        "additionalProperty": [
          { "@type": "PropertyValue", "name": "Battery Life", "value": "13 hours" },
          { "@type": "PropertyValue", "name": "Connectivity", "value": "4G LTE, Wi-Fi, BT 5.1" },
          { "@type": "PropertyValue", "name": "Durability", "value": "IP68, MIL-STD-810H" }
        ],
        "offers": {
          "@type": "Offer",
          "availability": "https://schema.org/InStock",
          "priceCurrency": "MYR",
          "seller": { "@type": "Organization", "name": "SE Distribution Sdn Bhd" }
        },
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "4.8",
          "reviewCount": "44",
          "bestRating": "5"
        }
      }
    },
    {
      "@type": "ListItem",
      "position": 3,
      "item": {
        "@type": "Product",
        "name": "Hytera GC550 Body-Worn Camera",
        "description": "Compact body camera — 2K (1440P), 9-hour battery, IP67, MIL-STD-810G, AES-256 encryption, infrared night vision, pre-event recording.",
        "brand": { "@type": "Brand", "name": "Hytera" },
        "sku": "GC550",
        "url": "https://www.sedistribution.com.my/hytera-bwv",
        "additionalProperty": [
          { "@type": "PropertyValue", "name": "Battery Life", "value": "9 hours" },
          { "@type": "PropertyValue", "name": "Durability", "value": "IP67, MIL-STD-810G" }
        ],
        "offers": {
          "@type": "Offer",
          "availability": "https://schema.org/InStock",
          "priceCurrency": "MYR",
          "seller": { "@type": "Organization", "name": "SE Distribution Sdn Bhd" }
        },
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "4.7",
          "reviewCount": "31",
          "bestRating": "5"
        }
      }
    }
  ]
};

const faqItems = [
  {
    q: "What is the difference between SC880 5G, SC780 4G, and GC550?",
    a: "The SC880 5G is the flagship — it offers 4K video, 5G live streaming, and a 300-second pre-record buffer. The SC780 4G is the most popular choice for extended patrols, with a 13-hour battery and 4G live streaming. The GC550 is the compact option — 2K recording, 9-hour battery, and a simple one-button operation ideal for wider security team deployments. All three include AES-256 encryption and are compatible with Hytera DEMS.",
  },
  {
    q: "Which Hytera BWC model is recommended for PDRM and Malaysian police?",
    a: "For frontline PDRM patrol units, we recommend the SC880 5G — 4K evidence capture, 5G live streaming to the command centre, and a 300-second pre-event buffer ensure nothing is missed before an incident escalates. For extended shift patrols, the SC780 4G's 13-hour battery is the standout choice. The GC550 suits auxiliary police and station-based personnel who need basic incident recording.",
  },
  {
    q: "Is the GC550 suitable for private security and retail deployments?",
    a: "Yes. The GC550 is specifically designed for cost-effective, wide-scale deployments — making it the preferred choice for retail security teams, mall officers, hospital security, transit personnel, and corporate campus teams. Its one-button simplicity, compact clip-on design, and AES-256 encryption make it practical for large security workforces.",
  },
  {
    q: "What is the pre-recording buffer and why does it matter?",
    a: "Pre-recording means the camera continuously buffers video in the background. When you press record, it saves footage from before you pressed the button — capturing what triggered the incident, not just what happened after. The SC880 buffers 300 seconds (5 minutes), while the SC780 and GC550 both buffer 120 seconds. This is critical for evidence admissibility in Malaysian courts.",
  },
  {
    q: "Can all three cameras work with the Hytera DEMS evidence platform?",
    a: "Yes. The SC880, SC780, and GC550 are all fully compatible with Hytera DEMS (Digital Evidence Management System). When docked at the end of a shift, footage automatically uploads to DEMS with GPS coordinates, officer ID, timestamps, and incident tags — creating a complete, tamper-proof chain of custody. DEMS supports on-premise or private cloud deployment.",
  },
  {
    q: "Does SE Distribution support government tender submissions for Hytera cameras?",
    a: "Yes. SE Distribution Sdn Bhd (Reg: 202001036939) is the authorized distributor for Hytera in Malaysia. We provide authorized distributor letters, compliance certificates, technical datasheets for SC880, SC780, and GC550, and full after-sales support documentation for government and public sector procurement. Contact our team for tender support.",
  },
  {
    q: "What is the battery life of each Hytera BWC model?",
    a: "SC880 5G — 10 hours at 1080P with 5G and GPS active. SC780 4G — 13 hours at 720P with GPS and LTE on, the longest battery in the lineup. GC550 — 9 hours at 720P continuous. All models support multi-bay docking stations for overnight charging and automatic DEMS upload.",
  },
  {
    q: "How is footage encrypted and secured on Hytera cameras?",
    a: "All three models — SC880, SC780, and GC550 — use AES-256 encryption with digital signatures and hash verification. This means footage is encrypted from the moment it is captured and cannot be decrypted or altered without authorised access. Combined with DEMS's immutable audit trail, the evidence chain is fully preserved for court proceedings.",
  },
];

function HyteraFAQ() {
  const [openIdx, setOpenIdx] = useState<number | null>(null);
  return (
    <div className="space-y-3">
      {faqItems.map((item, i) => (
        <div key={i} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <button
            onClick={() => setOpenIdx(openIdx === i ? null : i)}
            className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer hover:bg-[#fafaf9] transition-colors"
          >
            <span className="text-sm font-semibold text-[#1a1a1a] pr-4 leading-snug">{item.q}</span>
            <div className="w-7 h-7 flex items-center justify-center bg-[#1a1a1a]/5 rounded-full flex-shrink-0">
              <i className={`${openIdx === i ? "ri-subtract-line" : "ri-add-line"} text-[#1a1a1a] text-sm`}></i>
            </div>
          </button>
          {openIdx === i && (
            <div className="px-6 pb-5 border-t border-gray-50">
              <p className="text-sm text-gray-500 leading-relaxed pt-4">{item.a}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

const hyteraBreadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    {
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": "https://www.sedistribution.com.my/"
    },
    {
      "@type": "ListItem",
      "position": 2,
      "name": "Solutions",
      "item": "https://www.sedistribution.com.my/solutions"
    },
    {
      "@type": "ListItem",
      "position": 3,
      "name": "Body-Worn Video",
      "item": "https://www.sedistribution.com.my/hytera-bwv"
    }
  ]
};

export default function HyteraBWVPage() {
  useSEO({
    title: "Hytera BWC Malaysia | SC880 5G, SC780 4G & GC550 Body Camera | SE Distribution",
    description: "Hytera BWC Malaysia — SC880 5G (4K, 300s pre-record), SC780 4G (13-hr battery, live stream) & GC550 (compact, 1080P). Authorized distributor SE Distribution. DEMS evidence platform, multi-bay docking, full local support in KL. Trusted by PDRM, government agencies & security teams.",
    canonical: "https://www.sedistribution.com.my/hytera-bwv",
    keywords: "Hytera BWC Malaysia, SC880 5G body camera Malaysia, SC780 4G body camera Malaysia, GC550 body camera Malaysia, Hytera body worn camera, DEMS evidence management Malaysia, body worn camera law enforcement Malaysia, Hytera BWC distributor Malaysia, PDRM body camera",
    structuredData: hyteraBreadcrumbSchema,
  });
  const [activeProduct, setActiveProduct] = useState(0);

  return (
    <div className="min-h-screen bg-white">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(hyteraProductSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(hyteraFAQSchema) }}
      />
      <Navbar />
      <StickyQuoteButton label="Request a Demo" />
      <main className="pt-20">

        {/* Hero */}
        <section className="relative bg-[#1a1a1a] py-24 overflow-hidden">
          <img
            src="https://static.readdy.ai/image/8ef50732a893e16bbc98824684ca939c/6b1fd935f71dbae716442a817803f9ed.jpeg"
            alt="Hytera Body-Worn Video"
            className="absolute inset-0 w-full h-full object-cover object-top opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1a1a1a]/90 via-[#1a1a1a]/60 to-[#1a1a1a]/30"></div>
          <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
            <div className="max-w-2xl">
              <div className="flex items-center gap-3 mb-5">
                <img
                  src="https://storage.readdy-site.link/project_files/15616acb-dd20-42a3-bb13-24e14d98a32e/6d2ef031-b05d-445b-add7-cb1ee7ec1599_Hytera-Logo-RGB.png?v=2b451d70b2f39a7a3313fb4cfd9d1d6a"
                  alt="Hytera"
                  className="h-8 w-auto brightness-0 invert"
                />
                <span className="text-white/40 text-sm">|</span>
                <span className="text-xs font-semibold tracking-widest uppercase text-white/50">Body-Worn Camera Solutions</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
                Malaysia's Primary Choice for <strong>Body-Worn Camera</strong>
              </h1>
              <p className="text-white/65 text-base leading-relaxed mb-5">
                Hytera SC880 5G, SC780 4G &amp; GC550 — purpose-built body-worn cameras for law enforcement, government agencies, and security operations across Malaysia. Encrypted evidence capture, real-time streaming, and seamless digital evidence management in every model.
              </p>
              {/* Model Pills */}
              <div className="flex flex-wrap gap-2 mb-8">
                {[
                  { name: "SC880 5G", label: "4K · 5G · 10hr", badge: "Flagship", badgeColor: "text-amber-400" },
                  { name: "SC780 4G", label: "1080P · 4G · 13hr", badge: "Popular", badgeColor: "text-green-400" },
                  { name: "GC550", label: "2K · Compact · 9hr", badge: "Compact", badgeColor: "text-white/50" },
                ].map((m) => (
                  <div key={m.name} className="flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-3 py-1.5">
                    <span className={`text-[9px] font-bold uppercase tracking-widest ${m.badgeColor}`}>{m.badge}</span>
                    <span className="text-white/20 text-xs">·</span>
                    <span className="text-white text-xs font-semibold">{m.name}</span>
                    <span className="text-white/40 text-[10px]">{m.label}</span>
                  </div>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="bg-white text-[#1a1a1a] text-sm font-semibold px-7 py-3.5 rounded-md hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2"
                >
                  Request a Demo
                  <i className="ri-arrow-right-line"></i>
                </Link>
                <a
                  href="tel:+60364127917"
                  className="border border-white/40 text-white text-sm font-medium px-7 py-3.5 rounded-md hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2"
                >
                  <i className="ri-phone-line"></i>
                  +603 6412 7917
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Why Hytera */}
        <section className="py-20 bg-[#f9f7f4]">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="text-center mb-12">
              <span className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-3 block">Why Hytera BWC</span>
              <h2 className="text-3xl font-bold text-[#1a1a1a]">The Standard for Professional Body-Worn Camera</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {whyHytera.map((w) => (
                <div key={w.title} className="bg-white rounded-lg p-6 border border-gray-100">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#1a1a1a]/5 rounded-lg mb-4">
                    <i className={`${w.icon} text-[#1a1a1a] text-xl`}></i>
                  </div>
                  <h3 className="font-semibold text-[#1a1a1a] text-sm mb-2">{w.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{w.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Product Lineup */}
        <section className="py-20 max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <span className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-3 block">Product Lineup</span>
            <h2 className="text-3xl font-bold text-[#1a1a1a]">Hytera BWC Camera Range</h2>
            <p className="text-gray-500 text-sm mt-3 max-w-lg mx-auto">Three purpose-built models — SC880 5G flagship, SC780 4G workhorse, and GC550 compact — each delivering encrypted evidence capture for demanding operations.</p>
          </div>

          {/* Product Tabs */}
          <div className="flex gap-2 justify-center mb-10 flex-wrap">
            {products.map((p, i) => (
              <button
                key={p.id}
                onClick={() => setActiveProduct(i)}
                className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeProduct === i
                    ? "bg-[#1a1a1a] text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {p.name}
                {p.badge === "Flagship" && (
                  <span className="ml-2 text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">Flagship</span>
                )}
              </button>
            ))}
          </div>

          {/* Active Product Detail */}
          {products.map((p, i) => (
            <div
              key={p.id}
              className={`flex flex-col lg:flex-row gap-12 items-start transition-all ${activeProduct === i ? "block" : "hidden"}`}
            >
              {/* Image */}
              <div className="w-full lg:w-2/5 flex-shrink-0">
                <div className="w-full h-96 rounded-xl overflow-hidden bg-[#f9f7f4]">
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover object-top" />
                </div>
              </div>

              {/* Details */}
              <div className="w-full lg:w-3/5">
                <div className="flex items-center gap-3 mb-3">
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                    p.badge === "Flagship" ? "bg-amber-100 text-amber-700" :
                    p.badge === "Popular" ? "bg-green-100 text-green-700" :
                    "bg-gray-100 text-gray-600"
                  }`}>{p.badge}</span>
                </div>
                <h3 className="text-2xl font-bold text-[#1a1a1a] mb-1">{p.name}</h3>
                <p className="text-gray-400 text-sm mb-6">{p.tagline}</p>

                {/* Specs Grid */}
                <div className="grid grid-cols-2 gap-3 mb-6">
                  {p.specs.map((s) => (
                    <div key={s.label} className="bg-[#f9f7f4] rounded-lg p-3">
                      <p className="text-xs text-gray-400 mb-0.5">{s.label}</p>
                      <p className="text-sm font-semibold text-[#1a1a1a]">{s.value}</p>
                    </div>
                  ))}
                </div>

                {/* Features */}
                <div className="mb-7">
                  <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3">Key Features</p>
                  <ul className="space-y-2">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-sm text-gray-600">
                        <i className="ri-check-line text-[#1a1a1a] mt-0.5 flex-shrink-0"></i>
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="flex gap-3 flex-wrap">
                  <Link
                    to="/contact"
                    className="bg-[#1a1a1a] text-white text-sm font-semibold px-6 py-3 rounded-md hover:bg-[#333] transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Enquire About {p.name}
                  </Link>
                  <Link
                    to="/brands"
                    className="border border-gray-200 text-gray-600 text-sm font-medium px-6 py-3 rounded-md hover:border-[#1a1a1a] hover:text-[#1a1a1a] transition-colors whitespace-nowrap cursor-pointer"
                  >
                    View Hytera Brand Profile
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </section>

        {/* Model Comparison Table */}
        <section className="py-20 bg-[#f9f7f4]">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="text-center mb-12">
              <span className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-3 block">Model Comparison</span>
              <h2 className="text-3xl font-bold text-[#1a1a1a]">SC880 vs SC780 vs GC550 — Which Camera Fits Your Mission?</h2>
              <p className="text-gray-500 text-sm mt-3 max-w-xl mx-auto leading-relaxed">
                Compare all three models side by side to find the right fit for your unit's operational requirements and budget.
              </p>
            </div>

            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr>
                    <th className="text-left py-4 pr-6 text-xs font-bold uppercase tracking-widest text-gray-400 w-48">Specification</th>
                    {/* SC880 */}
                    <th className="py-4 px-6 bg-[#1a1a1a] rounded-tl-xl rounded-tr-xl text-center">
                      <div className="inline-flex flex-col items-center">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-amber-400 mb-1">Flagship</span>
                        <span className="text-white font-bold text-base">SC880 5G</span>
                        <span className="text-white/50 text-[10px] mt-0.5">4K · 5G · 10hr</span>
                      </div>
                    </th>
                    {/* SC780 */}
                    <th className="py-4 px-6 bg-white border border-gray-100 text-center">
                      <div className="inline-flex flex-col items-center">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-green-600 mb-1">Popular</span>
                        <span className="text-[#1a1a1a] font-bold text-base">SC780 4G</span>
                        <span className="text-gray-400 text-[10px] mt-0.5">1080P · 4G · 13hr</span>
                      </div>
                    </th>
                    {/* GC550 */}
                    <th className="py-4 px-6 bg-white border border-gray-100 rounded-tr-xl text-center">
                      <div className="inline-flex flex-col items-center">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1">Compact</span>
                        <span className="text-[#1a1a1a] font-bold text-base">GC550</span>
                        <span className="text-gray-400 text-[10px] mt-0.5">2K · 9hr</span>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    {
                      label: "Video Resolution",
                      icon: "ri-4k-line",
                      sc880: "4K / 2K / 1080P / 720P / 480P",
                      sc780: "1440P / 1080P / 720P / 480P",
                      gc550: "1440P / 1080P / 720P / 480P",
                      highlight: "sc880",
                    },
                    {
                      label: "Connectivity",
                      icon: "ri-signal-wifi-3-line",
                      sc880: "5G / Wi-Fi 6 / BT 5.2 / NFC",
                      sc780: "4G LTE / Wi-Fi / BT 5.1 / NFC",
                      gc550: "Wi-Fi / Bluetooth",
                      highlight: "sc880",
                    },
                    {
                      label: "Battery Life",
                      icon: "ri-battery-line",
                      sc880: "10 hours",
                      sc780: "13 hours",
                      gc550: "9 hours",
                      highlight: "sc780",
                    },
                    {
                      label: "Pre-Recording Buffer",
                      icon: "ri-timer-line",
                      sc880: "300 seconds",
                      sc780: "120 seconds",
                      gc550: "120 seconds",
                      highlight: "sc880",
                    },
                    {
                      label: "Water & Dust Rating",
                      icon: "ri-drop-line",
                      sc880: "IP68",
                      sc780: "IP68",
                      gc550: "IP67",
                      highlight: null,
                    },
                    {
                      label: "Durability Standard",
                      icon: "ri-shield-check-line",
                      sc880: "MIL-STD-810H",
                      sc780: "MIL-STD-810H",
                      gc550: "MIL-STD-810G",
                      highlight: null,
                    },
                    {
                      label: "Night Vision",
                      icon: "ri-moon-line",
                      sc880: "IR + Starlight Low-Light",
                      sc780: "IR Night Vision",
                      gc550: "IR Night Vision",
                      highlight: "sc880",
                    },
                    {
                      label: "Live Streaming",
                      icon: "ri-live-line",
                      sc880: "5G / 4G Real-Time",
                      sc780: "4G Real-Time",
                      gc550: "Wi-Fi Upload",
                      highlight: null,
                    },
                    {
                      label: "GPS Tracking",
                      icon: "ri-map-pin-line",
                      sc880: "GPS / BDS / GLONASS / Galileo",
                      sc780: "GPS / BDS / GLONASS / Galileo",
                      gc550: "GPS",
                      highlight: null,
                    },
                    {
                      label: "Encryption",
                      icon: "ri-shield-keyhole-line",
                      sc880: "AES-256",
                      sc780: "AES-256",
                      gc550: "AES-256",
                      highlight: null,
                    },
                    {
                      label: "DEMS Compatible",
                      icon: "ri-database-2-line",
                      sc880: "Yes",
                      sc780: "Yes",
                      gc550: "Yes",
                      highlight: null,
                    },
                    {
                      label: "Best For",
                      icon: "ri-user-star-line",
                      sc880: "Frontline patrol, PDRM, high-risk ops",
                      sc780: "Extended shifts, government enforcement",
                      gc550: "General security, cost-effective deployment",
                      highlight: null,
                    },
                  ].map((row, idx) => (
                    <tr key={row.label} className={idx % 2 === 0 ? "bg-white/60" : ""}>
                      <td className="py-3.5 pr-6">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 flex items-center justify-center bg-[#1a1a1a]/5 rounded-md flex-shrink-0">
                            <i className={`${row.icon} text-[#1a1a1a] text-sm`}></i>
                          </div>
                          <span className="text-xs font-semibold text-[#1a1a1a]">{row.label}</span>
                        </div>
                      </td>
                      <td className={`py-3.5 px-6 text-center text-xs ${
                        row.highlight === "sc880"
                          ? "bg-[#1a1a1a] text-white font-semibold"
                          : "bg-[#1a1a1a]/5 text-[#1a1a1a]"
                      }`}>
                        {row.sc880}
                      </td>
                      <td className={`py-3.5 px-6 text-center text-xs border-x border-gray-100 ${
                        row.highlight === "sc780"
                          ? "bg-green-50 text-green-800 font-semibold"
                          : "text-gray-600"
                      }`}>
                        {row.sc780}
                      </td>
                      <td className={`py-3.5 px-6 text-center text-xs ${
                        row.highlight === "gc550"
                          ? "bg-gray-100 text-[#1a1a1a] font-semibold"
                          : "text-gray-600"
                      }`}>
                        {row.gc550}
                      </td>
                    </tr>
                  ))}
                  {/* CTA Row */}
                  <tr>
                    <td className="pt-5 pr-6"></td>
                    <td className="pt-5 px-6 text-center bg-[#1a1a1a] rounded-bl-xl rounded-br-xl pb-5">
                      <a
                        href="/contact"
                        className="inline-flex items-center gap-1.5 bg-white text-[#1a1a1a] text-xs font-bold px-4 py-2 rounded-md hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        Enquire SC880 <i className="ri-arrow-right-line"></i>
                      </a>
                    </td>
                    <td className="pt-5 px-6 text-center border-x border-b border-gray-100 rounded-bl-xl rounded-br-xl pb-5">
                      <a
                        href="/contact"
                        className="inline-flex items-center gap-1.5 bg-[#1a1a1a] text-white text-xs font-bold px-4 py-2 rounded-md hover:bg-[#333] transition-colors whitespace-nowrap cursor-pointer"
                      >
                        Enquire SC780 <i className="ri-arrow-right-line"></i>
                      </a>
                    </td>
                    <td className="pt-5 px-6 text-center border-b border-gray-100 rounded-br-xl pb-5">
                      <a
                        href="/contact"
                        className="inline-flex items-center gap-1.5 bg-[#1a1a1a] text-white text-xs font-bold px-4 py-2 rounded-md hover:bg-[#333] transition-colors whitespace-nowrap cursor-pointer"
                      >
                        Enquire GC550 <i className="ri-arrow-right-line"></i>
                      </a>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden space-y-4">
              {[
                {
                  name: "SC880 5G",
                  badge: "Flagship",
                  badgeColor: "bg-amber-100 text-amber-700",
                  specs: [
                    { label: "Video", value: "4K Ultra HD" },
                    { label: "Battery", value: "10 hours" },
                    { label: "Network", value: "5G / Wi-Fi 6" },
                    { label: "Pre-Record", value: "300 seconds" },
                    { label: "Durability", value: "IP68 / MIL-STD-810H" },
                    { label: "Best For", value: "Frontline patrol & PDRM" },
                  ],
                },
                {
                  name: "SC780 4G",
                  badge: "Popular",
                  badgeColor: "bg-green-100 text-green-700",
                  specs: [
                    { label: "Video", value: "1080P Full HD" },
                    { label: "Battery", value: "13 hours (longest)" },
                    { label: "Network", value: "4G LTE / Wi-Fi" },
                    { label: "Pre-Record", value: "120 seconds" },
                    { label: "Durability", value: "IP68 / MIL-STD-810H" },
                    { label: "Best For", value: "Extended shifts & enforcement" },
                  ],
                },
                {
                  name: "GC550",
                  badge: "Compact",
                  badgeColor: "bg-gray-100 text-gray-600",
                  specs: [
                    { label: "Video", value: "2K (1440P) / 1080P" },
                    { label: "Battery", value: "9 hours" },
                    { label: "Network", value: "Wi-Fi Upload" },
                    { label: "Pre-Record", value: "120 seconds" },
                    { label: "Durability", value: "IP67 / MIL-STD-810G" },
                    { label: "Best For", value: "General security deployment" },
                  ],
                },
              ].map((model) => (
                <div key={model.name} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                  <div className="bg-[#1a1a1a] px-5 py-4 flex items-center justify-between">
                    <span className="font-bold text-white text-base">{model.name}</span>
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${model.badgeColor}`}>{model.badge}</span>
                  </div>
                  <div className="p-5 space-y-3">
                    {model.specs.map((s) => (
                      <div key={s.label} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                        <span className="text-xs text-gray-400 font-medium">{s.label}</span>
                        <span className="text-xs font-semibold text-[#1a1a1a] text-right max-w-[60%]">{s.value}</span>
                      </div>
                    ))}
                    <a
                      href="/contact"
                      className="w-full mt-2 flex items-center justify-center gap-2 bg-[#1a1a1a] text-white text-xs font-bold py-2.5 rounded-md hover:bg-[#333] transition-colors whitespace-nowrap cursor-pointer"
                    >
                      Enquire About {model.name} <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                </div>
              ))}
            </div>

            {/* Bottom note */}
            <div className="mt-10 text-center">
              <p className="text-xs text-gray-400 mb-4">Not sure which model is right for your unit? Our team will help you choose.</p>
              <a
                href="/contact"
                className="inline-flex items-center gap-2 border border-[#1a1a1a] text-[#1a1a1a] text-sm font-semibold px-6 py-3 rounded-md hover:bg-[#1a1a1a] hover:text-white transition-colors whitespace-nowrap cursor-pointer"
              >
                Get a Free Model Recommendation
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </section>

        {/* Product Deep-Dive — All Three Models */}
        <section className="py-20 px-6 lg:px-10 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <span className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-3 block">Product Deep-Dive</span>
              <h2 className="text-3xl font-bold text-[#1a1a1a]">Use Cases — Which Hytera BWC Fits Your Mission?</h2>
              <p className="text-gray-500 text-sm mt-3 max-w-xl mx-auto leading-relaxed">
                Each model is purpose-built for a specific operational profile. Explore the real-world use cases for the SC880, SC780, and GC550 to find the right fit for your unit.
              </p>
            </div>

            {/* ── SC880 5G ── */}
            <div className="mb-20">
              <div className="flex flex-col lg:flex-row gap-12 items-center mb-12">
                <div className="w-full lg:w-2/5 flex-shrink-0">
                  <div className="w-full h-[400px] rounded-xl overflow-hidden bg-[#f9f7f4]">
                    <img
                      src="https://static.readdy.ai/image/8ef50732a893e16bbc98824684ca939c/d4832d9eff2fe6174f7af255b86b27b9.jpeg"
                      alt="Hytera SC880 5G body camera"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
                <div className="w-full lg:w-3/5">
                  <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-full px-3 py-1 mb-4">
                    <i className="ri-medal-line text-amber-600 text-xs"></i>
                    <span className="text-xs font-bold text-amber-700 uppercase tracking-widest">SC880 5G — Flagship</span>
                  </div>
                  <h3 className="text-2xl font-bold text-[#1a1a1a] mb-2">4K Evidence. 5G Streaming. 300s Pre-Record.</h3>
                  <p className="text-gray-500 text-sm leading-relaxed mb-6">
                    The SC880 is designed for the most demanding frontline operations — PDRM patrol units, high-risk law enforcement, tactical teams, and government agencies where evidence quality and real-time command connectivity are non-negotiable.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
                    {[
                      { label: "Pre-Record", value: "300 Seconds" },
                      { label: "Video", value: "4K Ultra HD" },
                      { label: "Connectivity", value: "5G / Wi-Fi 6" },
                    ].map((s) => (
                      <div key={s.label} className="bg-[#f9f7f4] rounded-lg p-4 text-center border border-gray-100">
                        <p className="text-xs text-gray-400 mb-1">{s.label}</p>
                        <p className="text-sm font-bold text-[#1a1a1a]">{s.value}</p>
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[
                      { icon: "ri-vidicon-2-line", feat: "5G Real-Time Streaming", desc: "Live 4K feed to command centre — supervisors see exactly what the officer sees, in real time." },
                      { icon: "ri-timer-flash-line", feat: "300s Pre-Event Buffer", desc: "Captures 5 minutes before the button is pressed — the most critical evidence buffer in the lineup." },
                      { icon: "ri-sun-line", feat: "Starlight Low-Light Colour", desc: "Colour night vision — captures identifiable footage in near-darkness without switching to black-and-white IR." },
                      { icon: "ri-shield-check-line", feat: "AES-256 + Digital Signature", desc: "Every file signed and hashed — evidence is forensically verifiable and legally admissible." },
                    ].map((f) => (
                      <div key={f.feat} className="flex items-start gap-3 bg-[#f9f7f4] rounded-lg p-4 border border-gray-100">
                        <div className="w-8 h-8 flex items-center justify-center bg-[#1a1a1a] rounded-md flex-shrink-0 mt-0.5">
                          <i className={`${f.icon} text-white text-sm`}></i>
                        </div>
                        <div>
                          <p className="text-xs font-bold text-[#1a1a1a] mb-0.5">{f.feat}</p>
                          <p className="text-xs text-gray-500 leading-relaxed">{f.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* SC880 Use Cases */}
              <div className="border-t border-gray-100 pt-10">
                <div className="mb-8">
                  <span className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-2 block">SC880 Use Cases</span>
                  <h4 className="text-xl font-bold text-[#1a1a1a]">Who Is the SC880 5G Best For?</h4>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {[
                    {
                      icon: "ri-shield-user-line",
                      title: "PDRM Frontline Patrol",
                      desc: "The SC880 is the recommended choice for PDRM patrol units. 4K evidence with GPS metadata, 5G live streaming to the command centre, and a 300-second pre-event buffer ensure nothing is missed before an incident escalates.",
                      rec: "Primary recommendation for PDRM",
                    },
                    {
                      icon: "ri-spy-line",
                      title: "Tactical & High-Risk Operations",
                      desc: "Special operations teams and tactical units benefit from the SC880's 5G low-latency feed for real-time situational awareness, starlight colour night vision, and robust 1.5m drop-rated chassis.",
                      rec: "Recommended for tactical units",
                    },
                    {
                      icon: "ri-government-line",
                      title: "Government Enforcement Agencies",
                      desc: "Immigration, customs, and regulatory enforcement agencies use the SC880 for high-quality video evidence during field operations, border control, and compliance inspection with live command oversight.",
                      rec: "Recommended for federal agencies",
                    },
                    {
                      icon: "ri-map-pin-2-line",
                      title: "Border & Perimeter Security",
                      desc: "The SC880's multi-constellation GPS (GPS, BDS, GLONASS, Galileo) and 5G connectivity make it the strongest performer for wide-area perimeter patrols and border security where command oversight is critical.",
                      rec: "Recommended for border operations",
                    },
                    {
                      icon: "ri-building-4-line",
                      title: "High-Security Facilities",
                      desc: "Prisons, courts, military installations, and nuclear facilities deploy the SC880 for its highest-grade encryption and man-down auto-alert — ensuring officer safety and tamper-proof evidence in sensitive environments.",
                      rec: "Recommended for secure facilities",
                    },
                    {
                      icon: "ri-flashlight-line",
                      title: "Night & Low-Light Patrols",
                      desc: "Starlight colour night vision captures identifiable footage in near-total darkness — critical for night patrols, unlit industrial areas, and vehicular pursuits where standard IR cameras fail to capture usable colour evidence.",
                      rec: "Best low-light performance",
                    },
                  ].map((uc) => (
                    <div key={uc.title} className="bg-[#f9f7f4] rounded-xl p-6 border border-gray-100">
                      <div className="w-10 h-10 flex items-center justify-center bg-[#1a1a1a]/5 rounded-lg mb-4">
                        <i className={`${uc.icon} text-[#1a1a1a] text-xl`}></i>
                      </div>
                      <h4 className="font-bold text-[#1a1a1a] text-sm mb-2">{uc.title}</h4>
                      <p className="text-xs text-gray-500 leading-relaxed mb-3">{uc.desc}</p>
                      <div className="flex items-center gap-1.5">
                        <i className="ri-check-line text-amber-600 text-xs"></i>
                        <span className="text-[10px] font-semibold text-amber-700 uppercase tracking-wider">{uc.rec}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-8 flex justify-center">
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 bg-[#1a1a1a] text-white text-sm font-semibold px-6 py-3 rounded-md hover:bg-[#333] transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Enquire About SC880
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="border-t-2 border-dashed border-gray-100 mb-20"></div>

            {/* ── SC780 4G ── */}
            <div className="mb-20">
              <div className="flex flex-col lg:flex-row-reverse gap-12 items-center mb-12">
                <div className="w-full lg:w-2/5 flex-shrink-0">
                  <div className="w-full h-[400px] rounded-xl overflow-hidden bg-[#f9f7f4]">
                    <img
                      src="https://static.readdy.ai/image/8ef50732a893e16bbc98824684ca939c/4889e03a4b4bf32b74a96b78c69586ea.jpeg"
                      alt="Hytera SC780 4G body camera"
                      className="w-full h-full object-contain object-center"
                    />
                  </div>
                </div>
                <div className="w-full lg:w-3/5">
                  <div className="inline-flex items-center gap-2 bg-green-50 border border-green-200 rounded-full px-3 py-1 mb-4">
                    <i className="ri-award-fill text-green-600 text-xs"></i>
                    <span className="text-xs font-bold text-green-700 uppercase tracking-widest">SC780 4G — Most Popular</span>
                  </div>
                  <h3 className="text-2xl font-bold text-[#1a1a1a] mb-2">13-Hour Battery. 4G Live Streaming. 6-Axis Stabilisation.</h3>
                  <p className="text-gray-500 text-sm leading-relaxed mb-6">
                    The SC780 is the go-to workhorse for extended operations. Built for officers who need reliable 4G live streaming, image-stabilised 1080P evidence, and the longest battery in the Hytera lineup — 13 hours of continuous recording with GPS and LTE active.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
                    {[
                      { label: "Battery Life", value: "13 Hours" },
                      { label: "Video", value: "1080P Stabilised" },
                      { label: "Connectivity", value: "4G LTE / Wi-Fi" },
                    ].map((s) => (
                      <div key={s.label} className="bg-[#f9f7f4] rounded-lg p-4 text-center border border-gray-100">
                        <p className="text-xs text-gray-400 mb-1">{s.label}</p>
                        <p className="text-sm font-bold text-[#1a1a1a]">{s.value}</p>
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[
                      { icon: "ri-battery-2-charge-line", feat: "13-Hour Battery", desc: "Longest battery in the Hytera BWC lineup — outlasts a full double-shift without recharging." },
                      { icon: "ri-shake-hands-line", feat: "6-Axis Image Stabilisation", desc: "Rock-steady footage even during foot pursuits, vehicle movements, and physical confrontations." },
                      { icon: "ri-signal-wifi-3-line", feat: "4G Live Streaming + ABR", desc: "Adaptive Bitrate streaming automatically adjusts quality to available 4G bandwidth in the field." },
                      { icon: "ri-swap-box-line", feat: "NFC Officer ID Switch", desc: "Instant officer account swap at shift change via NFC tap — no manual login required." },
                    ].map((f) => (
                      <div key={f.feat} className="flex items-start gap-3 bg-[#f9f7f4] rounded-lg p-4 border border-gray-100">
                        <div className="w-8 h-8 flex items-center justify-center bg-[#1a1a1a] rounded-md flex-shrink-0 mt-0.5">
                          <i className={`${f.icon} text-white text-sm`}></i>
                        </div>
                        <div>
                          <p className="text-xs font-bold text-[#1a1a1a] mb-0.5">{f.feat}</p>
                          <p className="text-xs text-gray-500 leading-relaxed">{f.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* SC780 Use Cases */}
              <div className="border-t border-gray-100 pt-10">
                <div className="mb-8">
                  <span className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-2 block">SC780 Use Cases</span>
                  <h4 className="text-xl font-bold text-[#1a1a1a]">Who Is the SC780 4G Best For?</h4>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {[
                    {
                      icon: "ri-riding-line",
                      title: "Extended Shift Patrols",
                      desc: "With 13 hours of battery at 720P with GPS and 4G active, the SC780 is the natural choice for officers on double shifts, long-haul patrols, and assignments without access to charging infrastructure.",
                      rec: "Best battery life in lineup",
                    },
                    {
                      icon: "ri-building-4-line",
                      title: "Government Enforcement Officers",
                      desc: "Immigration and customs enforcement officers conducting lengthy field operations rely on the SC780's 4G live streaming and 120-second pre-record buffer for documentation and command-centre oversight throughout the day.",
                      rec: "Recommended for enforcement agencies",
                    },
                    {
                      icon: "ri-car-line",
                      title: "Traffic & Mobile Patrol Units",
                      desc: "The SC780's 6-axis image stabilisation delivers steady footage during vehicle stops, motorcycle pursuits, and traffic enforcement operations. Adaptive Bitrate streaming maintains 4G video quality on the move.",
                      rec: "Recommended for mobile patrol",
                    },
                    {
                      icon: "ri-group-line",
                      title: "Public Order & Crowd Management",
                      desc: "Public order units managing large gatherings, demonstrations, and crowd situations benefit from the SC780's wide 130° FOV and live 4G streaming — command centres can monitor crowd conditions in real time.",
                      rec: "Recommended for crowd management",
                    },
                    {
                      icon: "ri-customer-service-2-line",
                      title: "Community Policing Officers",
                      desc: "Officers on community liaison and neighbourhood patrol programmes use the SC780 for incident recording with PTT radio-over-cellular — maintaining communication with DMR and TETRA dispatch systems while recording.",
                      rec: "Recommended for community policing",
                    },
                    {
                      icon: "ri-map-2-line",
                      title: "Rural & Remote Operations",
                      desc: "4G LTE with Adaptive Bitrate ensures the SC780 maintains streaming even in areas with variable signal strength — ideal for enforcement operations in rural Malaysia, plantation regions, and remote industrial sites.",
                      rec: "Recommended for remote deployments",
                    },
                  ].map((uc) => (
                    <div key={uc.title} className="bg-[#f9f7f4] rounded-xl p-6 border border-gray-100">
                      <div className="w-10 h-10 flex items-center justify-center bg-[#1a1a1a]/5 rounded-lg mb-4">
                        <i className={`${uc.icon} text-[#1a1a1a] text-xl`}></i>
                      </div>
                      <h4 className="font-bold text-[#1a1a1a] text-sm mb-2">{uc.title}</h4>
                      <p className="text-xs text-gray-500 leading-relaxed mb-3">{uc.desc}</p>
                      <div className="flex items-center gap-1.5">
                        <i className="ri-check-line text-green-600 text-xs"></i>
                        <span className="text-[10px] font-semibold text-green-700 uppercase tracking-wider">{uc.rec}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-8 flex justify-center">
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 bg-[#1a1a1a] text-white text-sm font-semibold px-6 py-3 rounded-md hover:bg-[#333] transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Enquire About SC780
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="border-t-2 border-dashed border-gray-100 mb-20"></div>

            {/* ── GC550 ── */}
            <div>
              <div className="flex flex-col lg:flex-row gap-12 items-center mb-12">
                <div className="w-full lg:w-2/5 flex-shrink-0">
                  <div className="w-full h-[400px] rounded-xl overflow-hidden bg-[#f9f7f4]">
                    <img
                      src="https://static.readdy.ai/image/8ef50732a893e16bbc98824684ca939c/dd5187ed3d4f3dbc0fb8109d6d24b041.jpeg"
                      alt="Hytera GC550 body camera"
                      className="w-full h-full object-contain object-center"
                    />
                  </div>
                </div>
                <div className="w-full lg:w-3/5">
                  <div className="inline-flex items-center gap-2 bg-gray-100 border border-gray-200 rounded-full px-3 py-1 mb-4">
                    <i className="ri-camera-line text-gray-600 text-xs"></i>
                    <span className="text-xs font-bold text-gray-600 uppercase tracking-widest">GC550 — Compact</span>
                  </div>
                  <h3 className="text-2xl font-bold text-[#1a1a1a] mb-2">2K Recording. 9-Hour Battery. One-Button Simplicity.</h3>
                  <p className="text-gray-500 text-sm leading-relaxed mb-6">
                    The GC550 is built for organisations that need dependable body-worn video across a broad workforce — without complexity or premium pricing. One-button operation, 9-hour battery, infrared night vision, and full AES-256 encryption make it the most practical choice for large-scale security deployments.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
                    {[
                      { label: "Battery Life", value: "9 Hours" },
                      { label: "Video", value: "2K (1440P)" },
                      { label: "Night Vision", value: "IR + Starlight" },
                    ].map((s) => (
                      <div key={s.label} className="bg-[#f9f7f4] rounded-lg p-4 text-center border border-gray-100">
                        <p className="text-xs text-gray-400 mb-1">{s.label}</p>
                        <p className="text-sm font-bold text-[#1a1a1a]">{s.value}</p>
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[
                      { icon: "ri-record-circle-line", feat: "One-Button Operation", desc: "Press once to record — designed for stress-free activation by all security personnel regardless of tech experience." },
                      { icon: "ri-moon-line", feat: "Infrared Night Vision", desc: "IR LEDs auto-activate in low-light — captures identifiable footage in dark environments and night patrols." },
                      { icon: "ri-shield-keyhole-line", feat: "AES-256 Encryption", desc: "Every recording encrypted from capture — fully chain-of-custody compliant and court-admissible." },
                      { icon: "ri-database-2-line", feat: "DEMS Integration", desc: "Auto-uploads to Hytera DEMS on dock — metadata tagged, searchable, and audit-trail protected." },
                    ].map((f) => (
                      <div key={f.feat} className="flex items-start gap-3 bg-[#f9f7f4] rounded-lg p-4 border border-gray-100">
                        <div className="w-8 h-8 flex items-center justify-center bg-[#1a1a1a] rounded-md flex-shrink-0 mt-0.5">
                          <i className={`${f.icon} text-white text-sm`}></i>
                        </div>
                        <div>
                          <p className="text-xs font-bold text-[#1a1a1a] mb-0.5">{f.feat}</p>
                          <p className="text-xs text-gray-500 leading-relaxed">{f.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* GC550 Use Cases */}
              <div className="border-t border-gray-100 pt-10">
                <div className="mb-8">
                  <span className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-2 block">GC550 Use Cases</span>
                  <h4 className="text-xl font-bold text-[#1a1a1a]">Who Is the GC550 Best For?</h4>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
                  {[
                    {
                      icon: "ri-store-2-line",
                      title: "Retail & Mall Security",
                      desc: "Loss prevention officers and mall security teams benefit from the GC550's discreet clip-on design. Deters shoplifting and documents incidents with court-ready encrypted footage.",
                      rec: "Recommended model for retail",
                    },
                    {
                      icon: "ri-hospital-line",
                      title: "Hospital & Healthcare Security",
                      desc: "Healthcare security staff face confrontational situations regularly. The GC550 provides de-escalation evidence and incident documentation without the bulk of a flagship camera.",
                      rec: "Recommended for healthcare teams",
                    },
                    {
                      icon: "ri-train-line",
                      title: "Transit & Transport Security",
                      desc: "MRT, LRT, and bus rapid transit security teams use the GC550 for incident recording during fare disputes, crowd control, and anti-social behaviour management.",
                      rec: "Recommended for transit officers",
                    },
                    {
                      icon: "ri-building-2-line",
                      title: "Corporate & Campus Security",
                      desc: "Office buildings, industrial parks, and university campuses deploying security officers benefit from the GC550's ease of use and cost-effective per-unit pricing for large teams.",
                      rec: "Recommended for large team rollouts",
                    },
                    {
                      icon: "ri-bank-line",
                      title: "Banking & Financial Sector",
                      desc: "Bank security personnel and armoured car escorts use the GC550 to document cash handling operations, branch incidents, and customer disputes with full encryption.",
                      rec: "Recommended for financial security",
                    },
                    {
                      icon: "ri-government-line",
                      title: "Municipal & Enforcement Officers",
                      desc: "Local council enforcement officers, housing inspectors, and utility field staff use the GC550 to record compliance inspections and potentially confrontational field visits.",
                      rec: "Recommended for field enforcement",
                    },
                  ].map((uc) => (
                    <div key={uc.title} className="bg-[#f9f7f4] rounded-xl p-6 border border-gray-100">
                      <div className="w-10 h-10 flex items-center justify-center bg-[#1a1a1a]/5 rounded-lg mb-4">
                        <i className={`${uc.icon} text-[#1a1a1a] text-xl`}></i>
                      </div>
                      <h4 className="font-bold text-[#1a1a1a] text-sm mb-2">{uc.title}</h4>
                      <p className="text-xs text-gray-500 leading-relaxed mb-3">{uc.desc}</p>
                      <div className="flex items-center gap-1.5">
                        <i className="ri-check-line text-[#1a1a1a] text-xs"></i>
                        <span className="text-[10px] font-semibold text-[#1a1a1a] uppercase tracking-wider">{uc.rec}</span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Shared Accessories */}
                <div className="bg-[#1a1a1a] rounded-xl p-8">
                  <div className="text-center mb-8">
                    <span className="text-xs font-semibold tracking-widest uppercase text-white/40 mb-3 block">Accessories & Ecosystem</span>
                    <h3 className="text-xl font-bold text-white">Complete Your Hytera BWC Deployment</h3>
                    <p className="text-white/50 text-sm mt-2">Compatible accessories and ecosystem components for SC880, SC780 &amp; GC550</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    {[
                      {
                        icon: "ri-battery-charge-line",
                        title: "Multi-Bay Charging Station",
                        desc: "Charges multiple units simultaneously. Cameras fully charged and evidence auto-uploaded before next deployment. Compatible with all three models.",
                        tags: ["Multi-bay", "Auto-upload", "LAN connected"],
                      },
                      {
                        icon: "ri-t-shirt-line",
                        title: "Mounting Clip & Harness",
                        desc: "Universal chest clip and body harness options for secure, comfortable camera positioning across different uniform types and job roles.",
                        tags: ["Chest clip", "Body harness", "Uniform-compatible"],
                      },
                      {
                        icon: "ri-database-2-line",
                        title: "DEMS Evidence Platform",
                        desc: "Hytera DEMS centralises all evidence management — upload, tag, search, and audit footage from SC880, SC780, and GC550 on a single web-based platform.",
                        tags: ["Cloud", "Chain-of-custody", "AI search"],
                      },
                    ].map((acc) => (
                      <div key={acc.title} className="bg-white/5 border border-white/10 rounded-xl p-6">
                        <div className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-lg mb-4">
                          <i className={`${acc.icon} text-white text-xl`}></i>
                        </div>
                        <h4 className="font-bold text-white text-sm mb-2">{acc.title}</h4>
                        <p className="text-white/55 text-xs leading-relaxed mb-4">{acc.desc}</p>
                        <div className="flex flex-wrap gap-1.5">
                          {acc.tags.map((t) => (
                            <span key={t} className="text-[10px] font-semibold text-white/60 border border-white/20 px-2 py-0.5 rounded-full">{t}</span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-8 text-center">
                    <Link
                      to="/contact"
                      className="inline-flex items-center gap-2 bg-white text-[#1a1a1a] text-sm font-semibold px-6 py-3 rounded-md hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      Enquire About GC550 Deployment
                      <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 px-6 lg:px-10 bg-[#f9f7f4]">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-3 block">FAQ</span>
              <h2 className="text-3xl font-bold text-[#1a1a1a]">Frequently Asked Questions</h2>
              <p className="text-gray-500 text-sm mt-3">Common questions about the SC880, SC780, GC550 and Hytera BWC deployment in Malaysia.</p>
            </div>
            <HyteraFAQ />
          </div>
        </section>

        {/* Use Cases */}
        <section className="py-20 bg-[#1a1a1a]">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="text-center mb-12">
              <span className="text-xs font-semibold tracking-widest uppercase text-white/40 mb-3 block">Real-World Applications</span>
              <h2 className="text-3xl font-bold text-white">Who Uses Hytera BWC in Malaysia</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {useCases.map((u) => (
                <div key={u.title} className="bg-white/5 border border-white/10 rounded-lg p-6">
                  <div className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-lg mb-4">
                    <i className={`${u.icon} text-white text-xl`}></i>
                  </div>
                  <h3 className="font-semibold text-white text-sm mb-2">{u.title}</h3>
                  <p className="text-white/55 text-xs leading-relaxed mb-4">{u.desc}</p>
                  <div className="border-t border-white/10 pt-3">
                    <p className="text-xs font-semibold text-white/70">{u.stat}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Ecosystem Section — Docking Station + DEMS */}
        <section className="py-20 px-6 lg:px-10 bg-[#f9f7f4]">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-14">
              <span className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-3 block">Complete Ecosystem</span>
              <h2 className="text-3xl font-bold text-[#1a1a1a]">Beyond the Camera — The Full Hytera BWC System</h2>
              <p className="text-gray-500 text-sm mt-3 max-w-xl mx-auto leading-relaxed">
                A body-worn camera is only as powerful as the infrastructure behind it. Hytera's docking station and DEMS platform complete the chain — from capture to courtroom.
              </p>
            </div>

            {/* Docking Station */}
            <div className="flex flex-col lg:flex-row gap-12 items-center mb-16">
              <div className="w-full lg:w-1/2 h-80 rounded-xl overflow-hidden flex-shrink-0">
                <img
                  src="https://static.readdy.ai/image/8ef50732a893e16bbc98824684ca939c/762745233858c3b2b586e880b8c80bb7.jpeg"
                  alt="Hytera Docking Station"
                  className="w-full h-full object-contain object-center"
                />
              </div>
              <div className="w-full lg:w-1/2">
                <div className="inline-flex items-center gap-2 bg-[#1a1a1a]/5 rounded-full px-3 py-1 mb-4">
                  <i className="ri-charging-pile-2-line text-[#1a1a1a] text-xs"></i>
                  <span className="text-xs font-semibold text-[#1a1a1a] uppercase tracking-widest">Docking Station</span>
                </div>
                <h3 className="text-2xl font-bold text-[#1a1a1a] mb-2">Hytera Multi-Bay Docking Station</h3>
                <p className="text-gray-500 text-sm leading-relaxed mb-6">
                  The Hytera docking station is the operational backbone of any BWV deployment. Officers simply dock their camera at the end of a shift — the station automatically charges the device and uploads all encrypted footage to the DEMS platform without any manual intervention.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                  {[
                    { icon: "ri-upload-cloud-2-line", label: "Auto Upload", desc: "Footage uploads instantly on dock — no manual transfer needed" },
                    { icon: "ri-battery-charge-line", label: "Simultaneous Charging", desc: "Charges all cameras in the bay concurrently during upload" },
                    { icon: "ri-shield-keyhole-line", label: "Encrypted Transfer", desc: "AES-256 encrypted data transfer from camera to server" },
                    { icon: "ri-stack-line", label: "8-Bay Capacity", desc: "Supports up to 8 cameras per station, stackable for large teams" },
                  ].map((item) => (
                    <div key={item.label} className="flex items-start gap-3 bg-white rounded-lg p-4 border border-gray-100">
                      <div className="w-8 h-8 flex items-center justify-center bg-[#1a1a1a]/5 rounded-md flex-shrink-0 mt-0.5">
                        <i className={`${item.icon} text-[#1a1a1a] text-base`}></i>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-[#1a1a1a] mb-0.5">{item.label}</p>
                        <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2">
                  {["SC880 Compatible", "SC780 Compatible", "GC550 Compatible", "Stackable Design", "LAN Connected"].map((tag) => (
                    <span key={tag} className="text-xs font-medium bg-[#1a1a1a]/5 text-[#1a1a1a] px-3 py-1 rounded-full">{tag}</span>
                  ))}
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-gray-200 mb-16"></div>

            {/* DEMS Platform */}
            <div className="flex flex-col lg:flex-row-reverse gap-12 items-center">
              <div className="w-full lg:w-1/2 h-80 rounded-xl overflow-hidden flex-shrink-0">
                <img
                  src="https://static.readdy.ai/image/8ef50732a893e16bbc98824684ca939c/56b7b32d0169a2e2c61de445362338c8.jpeg"
                  alt="Hytera DEMS Platform"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="w-full lg:w-1/2">
                <div className="inline-flex items-center gap-2 bg-[#1a1a1a]/5 rounded-full px-3 py-1 mb-4">
                  <i className="ri-database-2-line text-[#1a1a1a] text-xs"></i>
                  <span className="text-xs font-semibold text-[#1a1a1a] uppercase tracking-widest">DEMS Platform</span>
                </div>
                <h3 className="text-2xl font-bold text-[#1a1a1a] mb-2">Hytera Digital Evidence Management System</h3>
                <p className="text-gray-500 text-sm leading-relaxed mb-6">
                  DEMS is Hytera's secure, web-based evidence management platform — the central hub where all footage is stored, tagged, searched, and shared. Designed to meet the strictest chain-of-custody requirements for legal admissibility, DEMS gives supervisors and investigators full control over every piece of captured evidence.
                </p>
                <ul className="space-y-3 mb-7">
                  {[
                    "Automatic encrypted upload via docking station — zero manual handling",
                    "Metadata tagging: officer ID, GPS location, timestamp, incident type",
                    "Role-based access control for officers, supervisors, and investigators",
                    "Full immutable audit trail for chain-of-custody legal compliance",
                    "AI-powered search by date, officer, location, or incident keyword",
                    "Secure footage sharing with prosecutors, legal teams, and courts",
                    "Configurable retention policies with automated archival",
                    "On-premise or private cloud deployment options",
                  ].map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-gray-600">
                      <i className="ri-check-line text-[#1a1a1a] mt-0.5 flex-shrink-0"></i>
                      {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-[#1a1a1a] text-white text-sm font-semibold px-6 py-3 rounded-md hover:bg-[#333] transition-colors whitespace-nowrap cursor-pointer"
                >
                  Request a DEMS Demo
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>

            {/* Ecosystem Flow */}
            <div className="mt-16 bg-white rounded-xl border border-gray-100 p-8">
              <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 text-center mb-8">How the Ecosystem Works</p>
              <div className="flex flex-col md:flex-row items-center justify-center gap-4">
                {[
                  { icon: "ri-vidicon-line", label: "Officer Records", sub: "SC880 / SC780 / GC550 captures encrypted footage in the field" },
                  { icon: "ri-arrow-right-line", label: "", sub: "", arrow: true },
                  { icon: "ri-charging-pile-2-line", label: "Camera Docked", sub: "Auto-upload begins the moment the camera is placed in the dock" },
                  { icon: "ri-arrow-right-line", label: "", sub: "", arrow: true },
                  { icon: "ri-cloud-line", label: "DEMS Receives", sub: "Footage is encrypted, tagged with metadata, and stored securely" },
                  { icon: "ri-arrow-right-line", label: "", sub: "", arrow: true },
                  { icon: "ri-search-eye-line", label: "Reviewed & Shared", sub: "Investigators access, search, and share footage with full audit trail" },
                ].map((step, idx) =>
                  step.arrow ? (
                    <div key={idx} className="hidden md:flex w-8 h-8 items-center justify-center flex-shrink-0">
                      <i className="ri-arrow-right-line text-gray-300 text-xl"></i>
                    </div>
                  ) : (
                    <div key={idx} className="flex flex-col items-center text-center flex-1 min-w-0 max-w-[160px]">
                      <div className="w-12 h-12 flex items-center justify-center bg-[#1a1a1a] rounded-xl mb-3">
                        <i className={`${step.icon} text-white text-xl`}></i>
                      </div>
                      <p className="text-xs font-bold text-[#1a1a1a] mb-1">{step.label}</p>
                      <p className="text-xs text-gray-400 leading-relaxed">{step.sub}</p>
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-20 bg-[#1a1a1a] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=professional%20security%20team%20briefing%20law%20enforcement%20officers%20body%20worn%20cameras%20equipment%2C%20dramatic%20lighting%2C%20dark%20professional%20atmosphere%2C%20wide%20shot%2C%20cinematic%20photography&width=1400&height=500&seq=bwv-cta1&orientation=landscape"
            alt="Deploy Hytera BWV"
            className="absolute inset-0 w-full h-full object-cover object-top opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a1a]/80 to-[#1a1a1a]/95"></div>
          <div className="relative max-w-7xl mx-auto px-6 lg:px-10 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Ready to Deploy Hytera BWC?</h2>
            <p className="text-white/60 text-sm max-w-lg mx-auto mb-8 leading-relaxed">
              As Malaysia's authorized Hytera distributor, SE Distribution handles everything from needs assessment and procurement to deployment, training, and ongoing support.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-white text-[#1a1a1a] text-sm font-semibold px-8 py-3.5 rounded-md hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                Get a Deployment Proposal
              </Link>
              <a
                href="tel:+60364127917"
                className="border border-white/40 text-white text-sm font-medium px-8 py-3.5 rounded-md hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
              >
                <i className="ri-phone-line"></i>
                Call +603 6412 7917
              </a>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
}
